import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ErrorState, LoadingState } from "@/components/app/AppUI";
import { leadRepository } from "@/data/repositories";
import type { Lead } from "@/domain/types";
import { Presentacion } from "@/features/presentacion/Presentacion";
import { puertosCierreSupabase } from "@/features/presentacion/puertosCierreSupabase";
import type { LeadDePresentacion } from "@/features/presentacion/tipos";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface BusquedaPresentacion {
  /** Lead del CRM con el que es la reunión. */
  lead?: string;
}

function validarBusqueda(search: Record<string, unknown>): BusquedaPresentacion {
  return typeof search.lead === "string" && UUID.test(search.lead) ? { lead: search.lead } : {};
}

export const Route = createFileRoute("/_authenticated/presentacion")({
  head: () => ({
    meta: [{ title: "Presentación · SUSIEDAD" }, { name: "robots", content: "noindex" }],
  }),
  validateSearch: validarBusqueda,
  // La ruta es la raíz de composición: aquí se decide que el cierre va a Supabase.
  component: PaginaPresentacion,
});

function leadDePresentacion(lead: Lead): LeadDePresentacion {
  return {
    id: lead.id,
    nombre: lead.name,
    email: lead.email ?? "",
    contacto: {
      telefono: lead.phone ?? "",
      fecha: lead.event_date ?? "",
      lugar: lead.location ?? "",
    },
  };
}

function PaginaPresentacion() {
  const { lead: leadId } = Route.useSearch();
  if (!leadId) return <Presentacion puertos={puertosCierreSupabase} />;
  return <PresentacionConLead leadId={leadId} />;
}

function PresentacionConLead({ leadId }: { leadId: string }) {
  const lead = useQuery({
    queryKey: ["lead", leadId],
    queryFn: () => leadRepository.get(leadId),
  });
  if (lead.isLoading) return <LoadingState label="Preparando la presentación" />;
  if (lead.error || !lead.data)
    return <ErrorState message={lead.error?.message ?? "No se encontró el lead"} />;
  return (
    <Presentacion
      key={lead.data.id}
      puertos={puertosCierreSupabase}
      lead={leadDePresentacion(lead.data)}
    />
  );
}
